import React, { useContext, useState } from 'react';
import Button from 'atomic/button';
import { useModal } from 'hooks/use-modal';
import Icon from 'atomic/icon';
import PriceHistoryChartRanges from 'modules/product/views/components/product-price-history-section/components/price-history-chart-ranges';
import Modal from 'components/modal';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import ProductPriceHistoryContext from 'modules/product/views/components/product-price-history-section/product-price-history.context';
import PriceAlertInterface from 'modules/price-alert/price-alert.interface';
import { useFetch } from 'hooks/use-fetch';
import PriceAlertModalConfirm from 'modules/product/views/components/product-price-history-section/components/price-alert-modal-confirm';

const PriceHistoryModalDesktop = React.lazy(() => import('./components/price-history-modal/price-history-modal.desktop'));
const PriceHistoryModalMobile = React.lazy(() => import('./components/price-history-modal/price-history-modal.mobile'));

import priceChart from './components/price-alert/assets/images/price-chart.svg';

import './product-price-history-section.scss';
import classNames from 'classnames';

interface ProductPriceHistorySectionProps {
    className: string;
    productId: number;
    productPrice: number;
}

const ProductPriceHistorySection: React.FC<ProductPriceHistorySectionProps> = ({ className, productId, productPrice }) => {
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const priceHistoryModal = useModal('PRICE_HISTORY_MODAL');
    const confirmAlert = useModal('ALERT_CONFIRM');
    const [range, setRange] = useState(14);
    const { post } = useFetch('/alert-cenowy/create');
    const isMobile = api.getDevice() === 'mobile';

    const onRangeChanged = (value: number): void => {
        setRange(value);
        priceHistoryModal.open();
    };

    const onSubmit = (formData: PriceAlertInterface): void => {
        post({ ...formData, productId }).then(confirmAlert.open);
    };

    return (
        <>
            <div className={classNames({
                [String(className)]: true,
                [`${className}--mobile`]: isMobile,
                'price-history-section': true,
                'price-history-section--mobile': isMobile
            })}
            >
                <div className="price-history-section__header">
                    <h2>
                        Historia cen
                    </h2>
                    <PriceHistoryChartRanges
                        range={range}
                        setRange={onRangeChanged}
                        shortList={true}
                    />
                </div>
                <Icon
                    className="price-history-section__chart__icon"
                    name="bar-chart-2"
                    color="secondary"
                    size={30}
                    onClick={priceHistoryModal.open}
                />
                <div className={classNames({
                    'price-history-section__chart': true
                })}
                >
                    <img
                        alt="Sprawdź historię i ustaw alert cenowy"
                        src={priceChart}
                        height={140}
                        width={285}
                        loading="lazy"
                    />

                    <div className={classNames({
                        'price-history-section__chart__button': true
                    })}
                    >
                        <Button
                            onClick={priceHistoryModal.open}
                            block={true}
                            color="transparent"
                            size="lg"
                        >
                            <Icon name="bar-chart-2" color="primary" size={18} />
                            Sprawdź historię cen
                        </Button>
                    </div>
                </div>
                { priceHistoryModal.isOpen && (
                    <Modal
                        showCloseIcon={false}
                        center={true}
                        isOpen={true}
                        onClose={priceHistoryModal.close}
                        shouldCloseOnOverlayClick={true}
                        modalClassName="price-history-modal"
                    >
                        <ProductPriceHistoryContext.Provider value={{ onSubmit }}>
                            <>
                                {isMobile
                                    ? (
                                        <PriceHistoryModalMobile
                                            onClose={priceHistoryModal.close}
                                            productId={productId}
                                            productPrice={productPrice}
                                            priceRange={range}
                                        />
                                    )
                                    : (
                                        <PriceHistoryModalDesktop
                                            onClose={priceHistoryModal.close}
                                            productId={productId}
                                            productPrice={productPrice}
                                            priceRange={range}
                                        />
                                    )
                                }
                            </>
                        </ProductPriceHistoryContext.Provider>
                    </Modal>
                )}
            </div>
            {
                confirmAlert.isOpen && (
                    <Modal
                        showCloseIcon={false}
                        center={true}
                        isOpen={true}
                        onClose={confirmAlert.close}
                        shouldCloseOnOverlayClick={true}
                        modalClassName="price-alert-modal-confirm"
                    >
                        <PriceAlertModalConfirm onClose={confirmAlert.close} />
                    </Modal>
                )
            }
        </>
    );
};

export default ProductPriceHistorySection;
